/**
 * ============================================================================
 * CORE CONTEXT: Theme State & Dark/Light Mode Provider
 * ============================================================================
 * Purpose:
 *   Manages global UI theme state (Dark mode vs Light mode) across the application.
 *   Synchronizes preferences with `localStorage` and toggles the `dark` CSS class
 *   on the root HTML document.
 * ============================================================================
 */

import React, { createContext, useContext, useState, useEffect } from "react";
import { STORAGE_KEYS } from "../config/constants";

const ThemeContext = createContext();

/**
 * Custom hook to consume the global theme context.
 *
 * @returns {{ isDark: boolean, toggleTheme: Function }} Theme context value
 */
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

/**
 * Theme Provider component wrapping the application.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children
 */
export const ThemeProvider = ({ children }) => {
  const [isDark, setIsDark] = useState(false);

  // Load saved theme preference on initial mount
  useEffect(() => {
    const savedTheme = localStorage.getItem(STORAGE_KEYS.THEME || "theme");
    if (savedTheme) {
      setIsDark(savedTheme === "dark");
    }
  }, []);

  // Sync class name on documentElement when theme changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.THEME || "theme", isDark ? "dark" : "light");
    if (isDark) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [isDark]);

  /**
   * Toggles between dark and light themes.
   */
  const toggleTheme = () => {
    setIsDark((prev) => !prev);
  };

  return (
    <ThemeContext.Provider value={{ isDark, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
